import { useAuthQuery } from 'app/api_v2/auth/queries';
import { getCurrentEnv } from 'app/helpers/getCurrentEnv';
import { isLoggedInAsAdmin } from 'app/helpers/isLoggedInAsAdmin';
import { AppState } from 'app/store';
import { setAuthData } from 'app/store/auth';
import { logoutUser } from 'app/store/auth/thunks/logout';
import { SplashScreen } from 'app/ui/components/SplashScreen';
import rg4js from 'raygun4js';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { isRaygunEnabled } from '../errors/errors.lib';

interface HasToBeLoggedInGuardProps {
  children: React.ReactNode;
}

export default function HasToBeLoggedInGuard(props: HasToBeLoggedInGuardProps) {
  const { children } = props;
  const { pathname } = useLocation();
  const user = useSelector(({ auth }: AppState) => auth.user);
  const dispatch = useDispatch();
  const [redirect, setRedirect] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    if (redirect !== '') {
      setRedirect('');
      navigate(redirect);
    }
  }, [redirect, navigate]);

  const { isLoading, isError, isSuccess, data } = useAuthQuery();

  useEffect(() => {
    if (isSuccess && data) {
      // if user is not already set, set auth data
      if (!user?.id) {
        dispatch(setAuthData(data));
        if (isRaygunEnabled()) {
          rg4js('setUser', {
            identifier: data.email, // A unique ID, email address, or another ID such as a UUID
            isAnonymous: false, // Indicates whether the user is anonymous or not
            uuid: data.id, // Optional device identifier
          });
        }
      }

      // when user changed in other window or tab, reload the page
      if (user?.id && user?.id !== data.id) {
        window.location.reload();
      }
    }
    if (isSuccess && !data) {
      setRedirect('/auth/logged-out');
    }
  }, [isSuccess, data, JSON.stringify(data), dispatch]);

  if (isLoading) {
    return <SplashScreen />;
  }

  if (!isLoading && isError) {
    dispatch(logoutUser());
    return (
      <Navigate
        to={{
          pathname: '/auth/logged-out',
        }}
        state={{ referrer: pathname }}
      />
    );
  }

  if (user?.id === '282' && getCurrentEnv() === 'production' && !isLoggedInAsAdmin()) {
    return <SplashScreen />;
  }

  if (isSuccess && data) {
    return <>{children}</>;
  }

  return null;
}
